<script>
  /**
   * BulkActionModal — confirm, run, and report a bulk action over selected users.
   *
   * Two run modes. `batch` calls `onRun(items)` once, for the reminder endpoint
   * that takes an array; there is no percentage to show, so the bar animates.
   * Otherwise `onRun(item)` runs once per item behind a determinate bar.
   *
   * `onRun` resolving truthy is success; any other resolved value, or a throw, is
   * a failure. The three user endpoints disagree about what success looks like —
   * clearCache resolves the string 'Empty' and carries no status — so callers
   * normalize and this component only asks "did it work?".
   *
   * Headings arrive pre-formatted rather than as i18n keys so callers can use ICU
   * plurals, which the unit-test i18n mock cannot render.
   *
   * @example
   * <BulkActionModal
   *   bind:show={showBulkAction}
   *   items={bulkTargets}
   *   title={$format('label.BULK_ACTION.CONFIRM_DISABLE', { values: { count: bulkTargets.length } })}
   *   warning={$format('label.BULK_ACTION.WARNING_DISABLE')}
   *   confirmLabel={$format('label.DISABLE_USER')}
   *   confirmColor="danger"
   *   onRun={(user) => runners.disable(user)}
   *   onFinish={onBulkActionFinish}
   * />
   */

  import { format } from 'svelte-i18n';
  import { Alert, Button, Modal, Progress } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { NavigationGuard } from '@client/components/navigation-guard';

  /**
   * Controls whether the modal is open. Use `bind:show`.
   * @type {boolean}
   */
  export let show = false;

  /**
   * The users to act on. Read once when the run starts.
   * @type {Object[]}
   */
  export let items = [];

  /**
   * Already-formatted confirm heading, e.g. "Disable 3 users?".
   * @type {string}
   */
  export let title = '';

  /**
   * Body text under the heading, explaining what the action does.
   * @type {string}
   */
  export let warning = '';

  /**
   * Optional second line, used to report users the action will skip.
   * @type {string}
   */
  export let note = '';

  /**
   * Already-formatted navigation-guard prompt. Defaults to the users wording.
   * @type {string}
   */
  export let leaveMessage = '';

  /**
   * Confirm button text, e.g. "Disable User".
   * @type {string}
   */
  export let confirmLabel = '';

  /**
   * Confirm button variant.
   * @type {string}
   */
  export let confirmColor = 'primary';

  /**
   * Running caption for batch runs only. Sequential runs format their own,
   * because the caption carries a live count.
   * @type {string}
   */
  export let progressLabel = '';

  /**
   * i18n key for the sequential running caption, formatted with `n` and `total`.
   * A key rather than a string because the caption carries a live count.
   * @type {string}
   */
  export let progressKey = 'label.BULK_ACTION.PROGRESS';

  /**
   * Run every item in a single `onRun(items)` call instead of one call per item.
   * @type {boolean}
   */
  export let batch = false;

  /**
   * Display name for the failure list.
   * @type {(item: Object) => string}
   */
  export let getName = (item) => item.name || item.email || '';

  /**
   * Performs the work. Resolving truthy is success; anything else, or a throw,
   * is a failure. Receives one item, or every item when `batch` is set.
   * @type {(target: Object | Object[]) => Promise<any>}
   */
  export let onRun = async () => true;

  /**
   * Called once when the outcome is dismissed, with the items that failed.
   * An empty array means everything succeeded.
   * @type {(failed: Object[]) => void}
   */
  export let onFinish = () => {};

  /** @type {'confirm' | 'running' | 'results'} */
  let phase = 'confirm';

  /** How many items have been attempted so far. */
  let completed = 0;

  /** @type {Object[]} */
  let failed = [];

  const reset = () => {
    phase = 'confirm';
    completed = 0;
    failed = [];
  };

  const finish = () => {
    onFinish(failed);
    show = false;
  };

  const cancel = () => {
    show = false;
  };

  const attempt = async (target) => {
    try {
      return !!(await onRun(target));
    } catch {
      return false;
    }
  };

  const run = async () => {
    phase = 'running';
    completed = 0;
    failed = [];

    if (batch) {
      // The call succeeds or fails as a whole, so a failure fails every item.
      if (!(await attempt(items))) {
        failed = [...items];
      }

      completed = items.length;
    } else {
      for (const item of items) {
        if (!(await attempt(item))) {
          failed = [...failed, item];
        }

        completed += 1;
      }
    }

    if (failed.length) {
      phase = 'results';
      return;
    }

    finish();
  };

  // The X and Escape are inert mid-run so a partly-applied action can't be orphaned.
  const toggle = () => {
    if (phase === 'running') {
      return;
    }

    if (phase === 'results') {
      finish();
      return;
    }

    cancel();
  };

  $: total = items.length;
  $: percent = total ? Math.ceil((completed / total) * 100) : 0;
  $: succeeded = completed - failed.length;
  $: show === false && reset();
</script>

<!-- Outside the Modal: sveltestrap portals its slot, and the guard must stay mounted regardless of portal timing. -->
<NavigationGuard when={phase === 'running'} message={leaveMessage || $format('label.BULK_ACTION.CONFIRM_LEAVE')} />

<Modal bind:isOpen={show} size="sm" {toggle} keyboard={phase !== 'running'}>
  <div class="p-4">
    {#if phase === 'confirm'}
      <h1 class="h5 mb-2">{title}</h1>
      <p class="text-muted" class:mb-2={note}>{warning}</p>

      {#if note}
        <p class="text-muted" data-testid="bulk-action-note">{note}</p>
      {/if}

      <div class="d-flex justify-content-end gap-2">
        <Button color="secondary" outline on:click={cancel}>{$format('label.CANCEL')}</Button>
        <Button color={confirmColor} disabled={phase !== 'confirm'} on:click={run}>{confirmLabel}</Button>
      </div>
    {:else if phase === 'running'}
      {#if batch}
        <Progress animated striped value={100} />
      {:else}
        <Progress value={percent} />
      {/if}

      <p class="text-center mt-3 mb-0" data-testid="bulk-action-progress">
        {batch ? progressLabel : $format(progressKey, { values: { n: completed, total } })}
      </p>
    {:else}
      {#if succeeded}
        <Alert color="success" class="d-flex align-items-center">
          <Icon name="check-circle" class="me-2" />
          {$format('label.BULK_ACTION.SUCCEEDED', { values: { count: succeeded, total } })}
        </Alert>
      {/if}

      <Alert color="danger">
        <div class="d-flex align-items-center">
          <Icon name="alert-triangle" class="me-2" />
          {$format('label.BULK_ACTION.FAILED', { values: { count: failed.length } })}
        </div>

        <ul class="mb-0 mt-2 ps-4" data-testid="bulk-action-failures">
          {#each failed as item}
            <li>{getName(item)}</li>
          {/each}
        </ul>
      </Alert>

      <div class="d-flex justify-content-end">
        <Button color="secondary" on:click={finish}>{$format('label.CLOSE')}</Button>
      </div>
    {/if}
  </div>
</Modal>
